import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import { performSelectionSearch } from '../../project-actions';
import ProjectCardList from '../../project-card-list/ProjectCardList';
import MaxWidthWrapper from '../../../components/max-width-wrapper/MaxWidthWrapper';
import { ContentTitle, SectionTitle, Title } from '../../../components/typography/Heading';
import { formatMoneyKr, formatNumber, pluralize } from '../../../utils';
import ThemeContainer from '../../../components/theme-container/ThemeContainer';
import ThemeContext from '../../../context/theme-context';
import classnames from 'classnames';
import { getBlockComponentForContent, THEMES } from '../../../utils/cms-utils';
import SelectionSearch from '../../../search/selection-search/SelectionSearch';
import { useMountedState } from '../../../components/hooks/mountedState';
import LoaderBar from '../../../components/loader-bar/LoaderBar';
import VisibilitySensor from '../../../components/visibility-sensor/VisibilitySensor';
import { trackEvent } from '../../../support-and-tracking/analytics-actions';
import ErrorMessage from '../../../components/info-messages/ErrorMessage';
import styles from './selection.module.less';
import ButtonText from '../../../components/button/button-text/ButtonText';
import CustomSBNNSelectionHeader from './CustomSBNNSelectionHeader';
import { useSearchParams } from 'react-router';

const Selection = ({
    projectIds,
    organizationIds,
    counties,
    externalServiceIds,
    tags,
    style,
    minAmount,
    randomizeProjects,
    activeFirst,
    bankName,
    showMatchingAmount,
    showTotalWithoutMatching,
    showTotalAmount,
    showCustomSBNNSelectionHeader,
    showNumberOfProjects,
    contentTitle = '',
    theme = { mainColor: THEMES.GREY },
    bottomContent = null,
    templateIds,
    eventIds,
    schoolId,
    hideOnNoProjects,
    subtitle = '',
    showSearch = false,
    showTextSearch = true,
    showLocationSearch = true,
    showAdvancedOptions = false,
    limit, // max number of projects shown
    limitShow = 4, // initial number of projects shown
    allowAutoLoading = false,
    allowAutoLoadingAfterFirstExpand = true,
    hideSearchWhenLessThanNumberOfHits = 5,
    ended,
    ...restProps
}) => {
    const dispatch = useDispatch();
    const props = restProps || {};

    const [projectIdList, setProjectIdList] = useMountedState([]);
    const [filteredCollectedAmount, setFilteredCollectedAmount] = useMountedState(null);
    const [filteredCollectedAmountWithoutMatching, setFilteredCollectedAmountWithoutMatching] = useMountedState(null);
    const [filteredMatchingAmount, setFilteredMatchingAmount] = useMountedState(null);
    const [filteredNumberOfProjects, setFilteredNumberOfProjects] = useMountedState(null);
    const [totalCollectedAmount, setTotalCollectedAmount] = useMountedState(null);
    const [totalMatchingAmount, setTotalMatchingAmount] = useMountedState(null);
    const [totalCollectedWithoutMatching, setTotalCollectedWithoutMatching] = useMountedState(null);
    const [totalNumberOfProjects, setTotalNumberOfProjects] = useMountedState(null);
    const [hasCompletedInitialFetch, setHasCompletedInitialFetch] = useMountedState(false);
    const [searchOffset, setSearchOffset] = useMountedState(0);
    const [searchParams] = useSearchParams();
    const initialSelectionCounty = searchParams.get('selectionCounty');
    const [currentSearchFilters, setCurrentSearchFilters] = useState({
        county: initialSelectionCounty,
        ended,
    });
    const [error, setError] = useState();

    const [isSearching, setIsSearching] = useState(false);

    const abortController = useRef(null);
    const canFetchMore = !limit || projectIdList.length < limit;
    const hasSearched = !_.isEmpty(currentSearchFilters);

    const resetSearchFilters = () => {
        const newSearchFilters = {
            county: initialSelectionCounty,
        };
        setCurrentSearchFilters(newSearchFilters);
        doSearch(newSearchFilters);
    };

    const doSearch = (options = currentSearchFilters) => {
        if (abortController.current) {
            abortController.current?.abort();
        }

        abortController.current = new AbortController();
        const abortSignal = abortController.current.signal;

        const {
            searchString = '',
            county = '',
            municipality = '',
            suburb = '',
            categories = props?.categories ?? [],
            receiverType,
            ended,
        } = options;

        let from = searchOffset;
        let currentProjectLists = projectIdList;
        if (!_.isEqual(currentSearchFilters, options)) {
            from = 0;
            setSearchOffset(0);
            currentProjectLists = [];
            setProjectIdList([]);
            setCurrentSearchFilters(options);
        } else if (!canFetchMore) {
            setIsSearching(false);
            return null;
        }

        if (from === 0) {
            trackEvent('selection', 'search', {
                projectIds,
                organizationIds,
                externalServiceIds,
                tags,
                counties,
                categories,
                bankName,
                ended,
                activeFirst,
                limit,
                templateIds,
                schoolId,
                eventIds,
                ...props,
                ...options,
            });
        }

        const limitShowMore = hasCompletedInitialFetch ? 10 : limitShow;
        const numberOfProjectsToFetch =
            limit && currentProjectLists.length + limitShowMore > limit
                ? limit - currentProjectLists.length
                : limitShowMore;
        setIsSearching(true);
        setError(null);
        dispatch(
            performSelectionSearch({
                contentTitle,
                projectIds,
                organizationIds,
                externalServiceIds,
                tags,
                counties,
                bankName,
                minAmount,
                randomizeProjects,
                activeFirst,
                max: numberOfProjectsToFetch,
                templateIds,
                schoolId,
                eventIds,
                showMatchingAmount: showMatchingAmount || showCustomSBNNSelectionHeader,
                showTotalWithoutMatching: showTotalWithoutMatching || showCustomSBNNSelectionHeader,
                showTotalAmount: showTotalAmount || showCustomSBNNSelectionHeader,
                categories: categories ?? [],
                searchString,
                county,
                municipality,
                suburb,
                from,
                signal: abortSignal,
                ...(ended === 'all' ? {} : { ended }),
                ...(receiverType === 'all' ? {} : { receiverType }),
            })
        )
            .then((result) => {
                if (!result || result?.abortSignal?.aborted) {
                    setIsSearching(false);
                    return;
                }
                const { projectIds, total, totalCollected, totalCollectedWithoutMatching, totalMatchingAmount } =
                    result;

                const extendedProjectIdsList = [...(currentProjectLists || []), ...(projectIds || [])];
                setProjectIdList(extendedProjectIdsList);
                setSearchOffset(extendedProjectIdsList.length);

                setFilteredNumberOfProjects(total);
                if (!hasCompletedInitialFetch) {
                    setTotalNumberOfProjects(total);
                }
                if (showTotalAmount || showCustomSBNNSelectionHeader) {
                    setFilteredCollectedAmount(totalCollected);
                    if (!hasCompletedInitialFetch) {
                        setTotalCollectedAmount(totalCollected);
                    }
                }
                if (showMatchingAmount || showCustomSBNNSelectionHeader) {
                    setFilteredMatchingAmount(totalMatchingAmount);
                    if (!hasCompletedInitialFetch) {
                        setTotalMatchingAmount(totalMatchingAmount);
                    }
                }
                if (showTotalWithoutMatching || showCustomSBNNSelectionHeader) {
                    setFilteredCollectedAmountWithoutMatching(totalCollectedWithoutMatching);
                    if (!hasCompletedInitialFetch) {
                        setTotalCollectedWithoutMatching(totalCollectedWithoutMatching);
                    }
                }

                setIsSearching(false);
                setHasCompletedInitialFetch(true);
                abortController.current = null;
            })
            .catch((err) => {
                if (err.name !== 'AbortError') {
                    setError(err?.message || 'Noe gikk galt, vennligst prøv igjen');
                }
            });
    };
    useEffect(() => {
        doSearch();
    }, [
        dispatch,
        projectIds,
        organizationIds,
        externalServiceIds,
        tags,
        minAmount,
        randomizeProjects,
        activeFirst,
        ended,
        limit,
        templateIds,
        schoolId,
    ]);

    if (hideOnNoProjects && !projectIdList?.length) {
        return null;
    }

    return (
        <ThemeContext.Provider value={{ theme: theme }}>
            <ThemeContainer className={styles.sectionWrapper}>
                <MaxWidthWrapper>
                    {contentTitle && <ContentTitle className={styles.title}>{contentTitle}</ContentTitle>}
                    {subtitle && (
                        <Title tag="p" className={styles.subtitle}>
                            {subtitle}
                        </Title>
                    )}
                    {showCustomSBNNSelectionHeader && (
                        <CustomSBNNSelectionHeader
                            collectedAmountWithoutMatching={filteredCollectedAmountWithoutMatching}
                            matchingAmount={filteredMatchingAmount}
                            totalCollectedAmount={filteredCollectedAmount}
                        />
                    )}
                    {showSearch && totalNumberOfProjects > hideSearchWhenLessThanNumberOfHits && (
                        <>
                            <SelectionSearch
                                className={styles.searchModule}
                                onSearch={doSearch}
                                onReset={resetSearchFilters}
                                selectedCategories={props.categories}
                                availableCounties={counties}
                                queryCounty={initialSelectionCounty}
                                showTextSearch={showTextSearch}
                                showLocationSearch={showLocationSearch}
                                showAdvancedOptions={showAdvancedOptions}
                                ended={props?.ended}
                            />
                            {hasSearched && (
                                <div className={styles.numberOfResults}>
                                    Fant {formatNumber(filteredNumberOfProjects)}{' '}
                                    {pluralize(filteredNumberOfProjects, 'spleis', 'spleiser')}
                                </div>
                            )}
                        </>
                    )}
                    {showTotalWithoutMatching &&
                        (filteredCollectedAmountWithoutMatching > 0 || totalCollectedWithoutMatching > 0) && (
                            <SectionTitle className={styles.title}>
                                {formatMoneyKr(filteredCollectedAmountWithoutMatching)} gitt av giverne
                            </SectionTitle>
                        )}
                    {showMatchingAmount && (filteredMatchingAmount > 0 || totalMatchingAmount > 0) && (
                        <SectionTitle className={styles.title}>
                            {formatMoneyKr(filteredMatchingAmount)} doblet
                        </SectionTitle>
                    )}
                    {showTotalAmount && (filteredCollectedAmount > 0 || totalCollectedAmount > 0) && (
                        <SectionTitle className={styles.title}>
                            {formatMoneyKr(filteredCollectedAmount)} samlet inn
                        </SectionTitle>
                    )}

                    {showNumberOfProjects && filteredNumberOfProjects > 0 && (!showSearch || !hasSearched) && (
                        <SectionTitle
                            className={classnames(
                                showNumberOfProjects &&
                                    filteredNumberOfProjects > 0 &&
                                    showTotalAmount &&
                                    filteredCollectedAmount > 0 &&
                                    styles.headingMargin,
                                styles.title
                            )}
                        >
                            {formatNumber(filteredNumberOfProjects)}{' '}
                            {pluralize(filteredNumberOfProjects, 'spleis', 'spleiser')}
                        </SectionTitle>
                    )}
                    {error && <ErrorMessage>{error}</ErrorMessage>}
                    <ProjectCardList projectIds={projectIdList} type={style} cardTitleHeadingLevel={3} />

                    <VisibilitySensor
                        active={projectIdList.length < filteredNumberOfProjects && !isSearching && canFetchMore}
                        onVisible={() => {
                            if (
                                projectIdList.length < filteredNumberOfProjects &&
                                !isSearching &&
                                !abortController.current &&
                                canFetchMore &&
                                (allowAutoLoading ||
                                    (allowAutoLoadingAfterFirstExpand && searchOffset > limitShow) ||
                                    totalNumberOfProjects !== filteredNumberOfProjects)
                            ) {
                                doSearch();
                            }
                        }}
                        offset={{ bottom: 300 }}
                    >
                        {projectIdList.length < filteredNumberOfProjects && isSearching && canFetchMore && (
                            <LoaderBar />
                        )}
                        {projectIdList.length < filteredNumberOfProjects && !isSearching && canFetchMore && (
                            <ButtonText onClick={() => doSearch(currentSearchFilters)}>Vis flere</ButtonText>
                        )}
                    </VisibilitySensor>

                    {bottomContent?.map((data) => (
                        <section key={data.sys.id}>{getBlockComponentForContent({ data })}</section>
                    ))}
                </MaxWidthWrapper>
            </ThemeContainer>
        </ThemeContext.Provider>
    );
};

export default Selection;
